import { useDataTableContext } from '../context';
import { DataTableColumnMeta } from '../types';
import { validateColumnFilterType } from '../utils';
import { RadioFilter } from './radio-filter';

// ─────────────────────────────────────
// Props
// ─────────────────────────────────────

interface ColumnRadioFilterProps {
  columnId: string;
  label: string;
  options: Array<{ value: string | null; label: string }>;
}

// ─────────────────────────────────────
// Component
// ─────────────────────────────────────

/**
 * Column-bound radio filter that syncs with DataTable's filter state.
 * Automatically validates column type compatibility and manages filter value.
 * Selecting the null-value option clears the filter.
 * @param {ColumnRadioFilterProps} props - Component props
 * @param {string} props.columnId - ID of the column to filter
 * @param {string} props.label - Display label for the filter
 * @param {Array} props.options - Available options with value/label pairs (use null value for "None")
 * @throws {Error} If column type is not 'enum' or 'boolean'
 * @return {JSX.Element} The column radio filter element
 */
export function ColumnRadioFilter({
  columnId,
  label,
  options,
}: ColumnRadioFilterProps) {
  const { table } = useDataTableContext();
  const column = table.getColumn(columnId);
  const meta = column?.columnDef.meta as DataTableColumnMeta | undefined;

  validateColumnFilterType('radio', meta?.type, columnId);

  // Radio stores a single value; column filter state uses string[] for consistency
  // with multiSelectFilter
  const rawValue = column?.getFilterValue();
  const filterArray = Array.isArray(rawValue) ? rawValue : [];
  const value = filterArray[0] ?? null;

  const handleChange = (newValue: string | null) => {
    column?.setFilterValue(newValue ? [newValue] : undefined);
  };

  return (
    <RadioFilter
      id={`${columnId}-filter`}
      label={label}
      value={value}
      onChange={handleChange}
      options={options}
    />
  );
}
